import { ChevronDown, EyeOff, Focus, Maximize2, X } from 'lucide-react';
import { toggleSelection, summarizeSelection, type ChartSelection } from '../../lib/chartSelection';
import { FeatureGate } from '../FeatureGate';
import { useEffect, useMemo, useState } from 'react';
import { AppCard } from '../ui/AppCard';
import { FullscreenOverlay } from '../ui/FullscreenOverlay';
import { SegmentedControl } from '../ui/SegmentedControl';
import { MultiSelect } from '../ui/MultiSelect';
import { BarChart } from './BarChart';
import { LineChart } from './LineChart';
import { CircularMetric } from './CircularMetric';
import { StackedBarChart } from './StackedBarChart';
import { GroupedBarChart } from './GroupedBarChart';
import { MultiLineChart } from './MultiLineChart';
import type { ChartScale } from './LinePlot';
import { MultiCircularMetric } from './MultiCircularMetric';
import { MarqueeText } from './MarqueeText';
import { CHART_VARIANTS, label as capitalize, type Channel, type ChannelRef, type ChartVariant, type Message, type Template } from '../../types';
import {
  buildBuckets,
  aggregateEntries,
  overallOfEntries,
  entriesFromMessages,
  hasNumericEntries,
  formatMetric,
  isAdditiveMetric,
  isChartableSignal,
  CHART_INTERVALS,
  CHART_METRICS,
  INTERVAL_LABELS,
  METRIC_LABELS,
  MINUTES_SPANS,
  type ChartEntry,
  type ChartInterval,
  type ChartMetric,
  type MinutesSpan,
} from '../../lib/chartBuckets';
import { useChartRollup } from '../../lib/useChartRollup';
import { useAutoSave } from '../../lib/useAutoSave';
import { useNowTick } from '../../lib/useNowTick';
import { usePersistedState, oneOf, stringArray } from '../../lib/usePersistedState';

const FALLBACK_SIGNAL_COLOR = '#6B7280';
// Signals with no template color (integration/agent signals) used to ALL fall
// back to one grey, so several lines were indistinguishable. A stable hash of
// the name picks a palette color instead - same name, same color, on every
// span, session and platform (iOS uses the identical djb2 + palette).
export const SIGNAL_PALETTE = ['#3B82F6', '#F59E0B', '#10B981', '#EF4444', '#8B5CF6', '#EC4899', '#14B8A6', '#F97316', '#84CC16', '#06B6D4', '#A855F7', '#EAB308'];
export function paletteColorFor(name: string): string {
  let h = 5381;
  for (let i = 0; i < name.length; i++) h = ((h * 33) ^ name.charCodeAt(i)) >>> 0;
  return SIGNAL_PALETTE[h % SIGNAL_PALETTE.length];
}
const QUARTER_MS = 15 * 60 * 1000;
// The backend's JSON layer can hand an empty array back as the string "[ ]"
// (see StoreContext/dataApi's coerce* helpers), so a stored list is only used
// when it really is an array of strings.
const HIDDEN_SERIES_CAP = 1000;
function namesFrom(v: unknown): string[] {
  return Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string').slice(0, HIDDEN_SERIES_CAP) : [];
}
// A message older than this (relative to when the rollup was computed) can't
// be a live signal - it's history the Messages tab paged in - so it must
// not be folded on top of a rollup that already contains it.
const LIVE_GRACE_MS = 5 * 60 * 1000;

// What the headline number next to the chart is called, per metric.
const METRIC_TOTAL_LABEL: Record<ChartMetric, string> = {
  count: 'in range',
  sum: 'total',
  last: 'latest',
  avg: 'average',
  min: 'min',
  max: 'max',
};

// Only meaningful while comparing channels (see `comparing`): 'channel' is
// one line per channel (its overall activity, ChartScreen's original
// shape). 'signal' pools every compared channel's chartable messages
// together and breaks THAT combined pool down by signal type instead -
// "Deploy" from channel A and "Deploy" from channel B become one summed
// line, not two, for a user asking to "mix all signals in one chart" by
// signal rather than by channel. 'signal-split' breaks down by signal type
// too, but keeps each channel's own line separate (same color per signal
// name, so e.g. "Outdoor" in two channels is still visually linked, but
// never summed/merged) - for a user who wants the per-signal color coding
// without losing which channel a line belongs to, e.g. two unrelated
// channels that happen to share a signal template/name.
export type CompareViewMode = 'channel' | 'signal' | 'signal-split';

// The full set of "Filters & settings" selections (everything below this
// component's own controlsOpen toggle) - what ChartScreen.tsx persists to
// the channel doc (Channel.chartSettings, via onSettingsChange) so a public
// share-link visitor sees the owner's actual configured view instead of
// always landing back on the hard-coded defaults. See `locked`/
// `initialSettings`/`onSettingsChange` on SignalActivityChartProps below.
export interface ChartSettings {
  interval: ChartInterval;
  minutesSpan: MinutesSpan;
  metric: ChartMetric;
  scale: '' | ChartScale;
  compareViewMode: CompareViewMode;
  signalTypes: string[];
  senders: string[];
  controlsOpen: boolean;
  // Legend entries the user switched off with the eye icon - their series are
  // not drawn. Stored with the rest of the channel's chart settings, so it
  // survives a reload, another device, and what a public viewer first sees.
  hiddenSeries?: string[];
}

// One channel's worth of comparison data - see ChartScreen.tsx, the only
// current source of these. `loading` shows a fetch-in-progress state for
// a just-added channel rather than momentarily rendering it as empty.
interface CompareChannel {
  channel: ChannelRef;
  messages: Message[];
  loading?: boolean;
}

interface Series {
  name: string;
  color: string;
  values: number[];
  total: number;
}

interface SignalActivityChartProps {
  messages: Message[];
  templates: Template[];
  chartType: ChartVariant;
  onChartTypeChange: (v: ChartVariant) => void;
  // Multi-channel chart comparison (distinct from Multi-Channel Reports -
  // this is pure client-side rendering, no backend job). When
  // `compareChannels` is non-empty, the chart overlays one series per
  // channel (this one plus each of `compareChannels`) instead of its
  // usual per-signal-type breakdown - comparing signal *types* across
  // channels with their own, usually unrelated, signal vocabularies
  // doesn't make sense, so the type/sender filters are hidden in this
  // mode. `primaryChannel` names/colors this component's own `messages`
  // as one of the series; omitted, it falls back to a neutral label/color.
  primaryChannel?: Channel;
  compareChannels?: CompareChannel[];
  // When set, the chart's first frame comes from the backend's
  // pre-aggregated rollups for this channel (whole history, cached) instead
  // of only the loaded messages; live `messages` are folded on top so it
  // keeps updating without refetching. Ignored while comparing channels or
  // filtering by sender (rollups don't carry a sender) - the chart then
  // computes from `messages` exactly as before.
  rollupChannelId?: string;
  // Overrides the localStorage key prefix the 7 "Filters & settings"
  // fields persist under (see `pk` below) - for a caller with neither
  // `rollupChannelId` nor `primaryChannel` to derive a per-channel key
  // from (PublicChannelViewScreen: a public visitor has no Channel doc,
  // just a nickname). Without this, every public channel a visitor opens
  // in the same browser would collide on the same "chart.shared" key -
  // visitor A's filter choice on one channel silently overwriting/
  // reappearing on an unrelated one.
  storageKey?: string;
  // Public-view read-only mode (PublicChannelViewScreen, gated by
  // Channel.publicChartFiltersAllowed - off by default) - hides the whole
  // "Filters & settings" panel (interval/span/compare-view/signal+sender
  // filters/scale/metric) so a visitor can look at the chart the owner
  // configured but can't change what it's showing. Maximize (fullscreen)
  // and clicking a bar/dot to see its exact value are NOT settings - both
  // stay available locked or not.
  locked?: boolean;
  // Seeds the 7 "Filters & settings" fields' initial value - the owner's
  // last-saved Channel.chartSettings, read by PublicChannelViewScreen off
  // the public SharedChannelView and handed in here so the public chart
  // renders the owner's actual configured view (not just the hard-coded
  // defaults below) even though `locked` hides the controls that would
  // otherwise let a visitor reach that same state themselves. Only read
  // once, as each field's usePersistedState default - see that hook's own
  // "only applies when localStorage has nothing stored yet" contract.
  initialSettings?: Partial<ChartSettings>;
  // Owner/interactive mode only (ChartScreen) - fired (debounced, see
  // useAutoSave below) whenever any of the 7 fields change, so
  // StoreContext.setChartSettings can persist them to the channel doc.
  // Omitted entirely in public/locked mode - nothing can change there.
  onSettingsChange?: (settings: ChartSettings) => void;
  // Opens the chart already in its full-screen overlay on mount - lets a
  // deep link (PublicChannelViewScreen's /shared/.../charts/full URL)
  // land straight in full screen instead of the visitor having to find
  // and click Maximize themselves. Only read once, as the internal
  // `fullscreen` state's initial value - this component owns the state
  // from then on (closing it is a normal Escape/X click, not a prop
  // change), so a parent that wants to stay in sync reads onFullscreenChange.
  startFullscreen?: boolean;
  // Fired whenever the full-screen overlay opens or closes (Maximize
  // click, X/Escape) - PublicChannelViewScreen uses this to keep the
  // shareable /charts/full URL in sync with what's actually on screen.
  onFullscreenChange?: (open: boolean) => void;
}

// The "signal activity over time" chart body - extracted out of
// screens/chat/ChartScreen.tsx so screens/channels/PublicChannelViewScreen
// can render the exact same chart from a channel's public messages/templates
// (fetched, not store-bound) without duplicating this logic. ChartScreen
// stays the store-bound wrapper (owns persisting chartType, adds the
// Reports button); this component only ever reads its props.
export function SignalActivityChart({
  messages,
  templates,
  chartType,
  onChartTypeChange,
  primaryChannel,
  compareChannels,
  rollupChannelId,
  storageKey,
  locked,
  initialSettings,
  onSettingsChange,
  startFullscreen,
  onFullscreenChange,
}: SignalActivityChartProps) {
  // Every selection persists to localStorage per channel, so the panel
  // reopens exactly as it was left (lib/usePersistedState.ts). `initialSettings`
  // (the owner's backend-saved Channel.chartSettings) only ever matters as
  // each hook's default - i.e. the first time this channel is opened on this
  // browser, with nothing in localStorage yet; a public/locked viewer has no
  // controls to ever diverge from it anyway.
  const pk = storageKey ?? `chart.${rollupChannelId ?? primaryChannel?.id ?? 'shared'}`;
  const [interval, setInterval] = usePersistedState<ChartInterval>(`${pk}.interval`, initialSettings?.interval ?? 'Day', oneOf(CHART_INTERVALS));
  const [minutesSpan, setMinutesSpan] = usePersistedState<MinutesSpan>(
    `${pk}.span`,
    initialSettings?.minutesSpan ?? '60m',
    oneOf(MINUTES_SPANS?.map((s) => s.value))
  );
  // Empty array = no filter applied (matches the old ALL_SIGNALS/no-filter
  // default) - see MultiSelect.tsx.
  const [selectedSignalTypes, setSelectedSignalTypes] = usePersistedState<string[]>(`${pk}.types`, initialSettings?.signalTypes ?? [], stringArray);
  const [selectedSenders, setSelectedSenders] = usePersistedState<string[]>(`${pk}.senders`, initialSettings?.senders ?? [], stringArray);
  const [controlsOpen, setControlsOpen] = usePersistedState<boolean>(`${pk}.controlsOpen`, initialSettings?.controlsOpen ?? true, (v) => typeof v === 'boolean');
  const [metric, setMetric] = usePersistedState<ChartMetric>(`${pk}.metric`, initialSettings?.metric ?? 'count', oneOf(CHART_METRICS));
  // '' = the user hasn't chosen: default per metric (see effectiveScale).
  const [scaleChoice, setScaleChoice] = usePersistedState<'' | ChartScale>(`${pk}.scale`, initialSettings?.scale ?? '', oneOf(['', 'shared', 'each'] as const));
  const [compareViewMode, setCompareViewMode] = usePersistedState<CompareViewMode>(
    `${pk}.compareView`,
    initialSettings?.compareViewMode ?? 'channel',
    oneOf(['channel', 'signal', 'signal-split'] as const)
  );

  // Series switched off in the legend (the eye icon). Persisted like the
  // fields above and mirrored to the channel doc below.
  const [hiddenSeries, setHiddenSeries] = usePersistedState<string[]>(`${pk}.hidden`, namesFrom(initialSettings?.hiddenSeries), stringArray);

  // The channel's saved selection is the source of truth: whenever the backend
  // has one (on open, or when it changes - another device, the owner editing
  // while a visitor looks), it replaces whatever an older local copy holds.
  // Keyed on the value, so the owner's own just-clicked change is not reverted
  // while its save is still in flight.
  const savedHiddenKey = initialSettings?.hiddenSeries === undefined ? null : JSON.stringify(namesFrom(initialSettings.hiddenSeries));
  useEffect(() => {
    if (savedHiddenKey === null) return;
    setHiddenSeries((prev) => (JSON.stringify(prev) === savedHiddenKey ? prev : (JSON.parse(savedHiddenKey) as string[])));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedHiddenKey]);

  // Mirrors the 7 fields above to the channel doc (owner/interactive mode
  // only - PublicChannelViewScreen never passes onSettingsChange, so this
  // is inert there). Debounced (same 800ms default as useAutoSave's other
  // callers) so rapid changes - e.g. selecting several signal types in a
  // row - collapse into one save instead of one PATCH per click.
  useAutoSave(
    useMemo<ChartSettings>(
      () => ({ interval, minutesSpan, metric, scale: scaleChoice, compareViewMode, signalTypes: selectedSignalTypes, senders: selectedSenders, controlsOpen, hiddenSeries }),
      [interval, minutesSpan, metric, scaleChoice, compareViewMode, selectedSignalTypes, selectedSenders, controlsOpen, hiddenSeries]
    ),
    onSettingsChange ?? (() => {}),
    { enabled: !!onSettingsChange }
  );

  // Hovering (mouse) or tapping (touch/click) a legend row highlights that
  // series on the chart and dims the rest - hover wins while active (so a
  // mouse user gets instant feedback without a click), a tap toggles a
  // "pinned" highlight that sticks around for touch devices, which have no
  // hover state at all. Tapping the same row again, or the empty space
  // outside the legend, un-pins it.
  const [hoveredSeries, setHoveredSeries] = useState<string | null>(null);
  const [pinnedSeries, setPinnedSeries] = useState<string | null>(null);

  // Full-screen "full detalization" view of the chart - same data, a
  // bigger chart and an Escape/close button, rendered in a viewport
  // overlay (FullscreenOverlay) rather than cramped inside the card.
  // `startFullscreen` only seeds the initial value (a deep link landing
  // straight in full screen) - see its own comment on
  // SignalActivityChartProps. `setFullscreenState` is every actual state
  // change from then on, wrapped so `onFullscreenChange` always mirrors it.
  const [fullscreen, setFullscreenState] = useState(!!startFullscreen);
  const setFullscreen = (open: boolean) => {
    setFullscreenState(open);
    onFullscreenChange?.(open);
  };

  // A clicked bar/segment/dot, pinned until clicked again or the
  // underlying data changes out from under it - drives the detail panel
  // below the chart (exact bucket label + every series' number there).
  // 'single' is the key used when there's only one series on the chart
  // (not comparing/breaking down), since there's no real series name then.
  // Several elements can be pinned at once: clicking toggles one in or out.
  const [selection, setSelection] = useState<ChartSelection[]>([]);
  const toggleSelected = (key: string, index: number) => setSelection((p) => toggleSelection(p, { key, index }));

  // Comparing channels takes priority over the per-signal-type breakdown
  // below: the type/sender filters are hidden in this mode (comparing
  // signal *types* across channels with their own, usually unrelated,
  // vocabularies doesn't make sense), so they simply stay at their empty
  // "no filter" default while comparing.
  const comparing = !!compareChannels && compareChannels.length > 0;

  const signalMessages = useMemo(() => messages.filter(isChartableSignal), [messages]);
  const availableSenders = useMemo(() => Array.from(new Set(signalMessages?.map((m) => m.sender))), [signalMessages]);

  // ---- data source: server rollups (+ live messages) or messages only ----
  // Used to also require `!comparing`: while comparing, this channel's own
  // series fell back to whatever's in `messages` - the store's loaded
  // window (DEFAULT_PAGE_SIZE, currently 20) - instead of its full rollup
  // history, so a busy channel's compare chart looked like it had almost no
  // history even with real activity going back well past the selected
  // window. Rollups don't carry a sender, which is the one real conflict.
  const wantRollup = !!rollupChannelId && selectedSenders.length === 0;
  const tier = interval === 'Minutes' ? 'm' : 'q';
  // Window start for the rollup request, floored so the request key is
  // stable across renders. "All" asks for the whole history.
  // `nowMs` advances on a timer so the bucket window follows the clock
  // instead of freezing at whatever time the panel was opened (which made
  // every signal after that fall past the last bucket).
  const nowMs = useNowTick(interval === 'Minutes' ? 15_000 : 60_000);
  const rollupFromMs = useMemo(() => {
    if (interval === 'All') return 0;
    const first = buildBuckets(interval, [], { minutesSpan, now: new Date(nowMs) })[0].start;
    return Math.floor(first / QUARTER_MS) * QUARTER_MS;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [interval, minutesSpan, Math.floor(nowMs / QUARTER_MS)]);
  const { data: rollup, loading: rollupLoading } = useChartRollup(wantRollup ? rollupChannelId : undefined, tier, rollupFromMs, messages);

  // Same fix as `wantRollup` above, for the OTHER side of a comparison: each
  // compared channel only ever had ChartScreen's first page of its signals
  // (`compareChannels[i].messages`, capped at DEFAULT_PAGE_SIZE) to chart
  // from, so a compared channel with real history looked empty past its
  // most recent handful of messages too. Each compared channel gets its own
  // rollup, same as the primary one, folding its own loaded messages on top
  // for anything too recent to be in the rollup yet.
  //
  // A fixed number of unconditional hook calls (Rules of Hooks: the same
  // hooks, in the same order, every render - a `.map` over `compareChannels`
  // itself, whose length can change between renders, would violate that).
  // 4 matches ChartScreen's own MAX_ADDITIONAL_CHANNELS cap, so this never
  // actually truncates a real comparison.
  const compareRollup0 = useChartRollup(comparing ? compareChannels?.[0]?.channel.id : undefined, tier, rollupFromMs, compareChannels?.[0]?.messages ?? []);
  const compareRollup1 = useChartRollup(comparing ? compareChannels?.[1]?.channel.id : undefined, tier, rollupFromMs, compareChannels?.[1]?.messages ?? []);
  const compareRollup2 = useChartRollup(comparing ? compareChannels?.[2]?.channel.id : undefined, tier, rollupFromMs, compareChannels?.[2]?.messages ?? []);
  const compareRollup3 = useChartRollup(comparing ? compareChannels?.[3]?.channel.id : undefined, tier, rollupFromMs, compareChannels?.[3]?.messages ?? []);
  const compareRollups = [compareRollup0, compareRollup1, compareRollup2, compareRollup3];
  const compareRollupLoading = comparing && compareChannels!.some((_, i) => compareRollups[i]?.loading);

  // The primary channel's entries, sender-filtered, before the type filter.
  const baseEntries = useMemo<ChartEntry[]>(() => {
    const senderFiltered =
      selectedSenders.length === 0 ? signalMessages : signalMessages.filter((m) => selectedSenders.includes(m.sender));
    // A rollup with zero entries is trusted the same as no rollup at all -
    // if it returned genuinely empty (a channel whose rollups haven't
    // backfilled yet, a from/tier combo the server has no slots for, ...),
    // the "already counted, skip it" `covered` logic below would otherwise
    // silently drop every one of this channel's real loaded messages
    // instead of ever falling back to them.
    if (!wantRollup || !rollup || rollup.entries.length === 0) return entriesFromMessages(senderFiltered);
    // Server rollup + only the messages it can't already contain.
    const live = signalMessages.filter(
      (m) => !rollup.covered.has(m.id) && new Date(m.at ?? m.date).getTime() >= rollup.asOf - LIVE_GRACE_MS
    );
    return [...rollup.entries, ...entriesFromMessages(live)];
  }, [wantRollup, rollup, signalMessages, selectedSenders]);

  const availableSignals = useMemo(() => Array.from(new Set(baseEntries?.map((e) => e.type))), [baseEntries]);
  const filteredEntries = useMemo(
    () => (selectedSignalTypes.length === 0 ? baseEntries : baseEntries.filter((e) => selectedSignalTypes.includes(e.type))),
    [baseEntries, selectedSignalTypes]
  );
  const compareEntries = useMemo(
    () =>
      comparing
        ? compareChannels!.map((cc, i) => {
            const stat = compareRollups[i]?.data;
            // Same "don't trust an empty rollup" guard as `baseEntries` -
            // see its comment. Without it, a compared channel whose rollup
            // came back empty (rather than null/failed) rendered as if it
            // had no history at all, even with real messages loaded.
            if (!stat || stat.entries.length === 0) return entriesFromMessages(cc.messages);
            // Same fold as `baseEntries`: the rollup's own history, plus
            // whatever's in this channel's loaded messages that's too new
            // for the rollup to have counted yet.
            const live = cc.messages.filter(
              (m) => isChartableSignal(m) && !stat.covered.has(m.id) && new Date(m.at ?? m.date).getTime() >= stat.asOf - LIVE_GRACE_MS
            );
            return [...stat.entries, ...entriesFromMessages(live)];
          })
        : [],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [comparing, compareChannels, compareRollup0.data, compareRollup1.data, compareRollup2.data, compareRollup3.data]
  );

  // Only offered once something's actually been sent with a value - most
  // templates never use TemplateSignal.promptForValue/static value, so a
  // toggle that'd always show "0" isn't worth surfacing.
  const canChartByValue = useMemo(() => hasNumericEntries(baseEntries), [baseEntries]);
  const activeMetric: ChartMetric = canChartByValue ? metric : 'count';

  // For "All", buildBuckets spans from the earliest signal to now - while
  // comparing, that needs to look across every compared channel's
  // signals too, or a channel with a longer history than the primary one
  // would render clipped/misaligned against a shared time axis it never
  // actually got a say in.
  const earliestMs = useMemo(() => {
    let min = Infinity;
    for (const e of baseEntries) if (e.t < min) min = e.t;
    for (const list of compareEntries) for (const e of list) if (e.t < min) min = e.t;
    return Number.isFinite(min) ? min : undefined;
  }, [baseEntries, compareEntries]);
  // Rebuilt from the ticking clock, but only handed on (new identity) when
  // the window actually moved, so downstream memos don't recompute every tick.
  const builtBuckets = buildBuckets(interval, [], { minutesSpan, earliestMs, now: new Date(nowMs) });
  const bucketsSignature = `${builtBuckets.length}|${builtBuckets[0]?.start}|${builtBuckets[builtBuckets.length - 1]?.end}`;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const buckets = useMemo(() => builtBuckets, [bucketsSignature]);
  const counts = useMemo(() => aggregateEntries(buckets, filteredEntries, activeMetric), [buckets, filteredEntries, activeMetric]);
  const total = overallOfEntries(buckets, filteredEntries, activeMetric);
  const signalShare = messages.length ? signalMessages.length / messages.length : 0;

  // Each signal carries its own color (TemplateSignal.colorHex) - looked up
  // by name across the templates passed in. ChartScreen prepends the
  // channel's own fully-hydrated templates ahead of the capped global
  // recency list, so a channel whose template is outside that window still
  // renders its real colors instead of the grey fallback.
  const colorFor = (signalName: string): string => {
    for (const t of templates) {
      const match = t.signals.find((s) => s.name === signalName);
      if (match) return match.colorHex;
    }
    return signalName ? paletteColorFor(signalName) : FALLBACK_SIGNAL_COLOR;
  };

  const makeSeries = (name: string, color: string, entries: ChartEntry[]): Series => ({
    name,
    color,
    values: aggregateEntries(buckets, entries, activeMetric),
    total: overallOfEntries(buckets, entries, activeMetric),
  });

  // A single selected type renders as one line/bar/percentage (below);
  // anything else - none selected (the old "all signals" default) or
  // several selected - renders as a per-type breakdown instead, same
  // shape the old all-signals view always used, just over whichever
  // types are actually selected.
  const singleSelectedType = selectedSignalTypes.length === 1 ? selectedSignalTypes[0] : null;
  const breakdownTypes = selectedSignalTypes.length > 0 ? selectedSignalTypes : availableSignals;

  const seriesBySignal = useMemo(() => {
    if (singleSelectedType) return null;
    return breakdownTypes.map((name) =>
      makeSeries(name, colorFor(name), baseEntries.filter((e) => e.type === name))
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [singleSelectedType, breakdownTypes, baseEntries, buckets, activeMetric]);

  const showingAllSignals = !singleSelectedType && !!seriesBySignal && seriesBySignal.length > 0;

  // One series per compared channel (this component's own `messages` as
  // the first, via `primaryChannel`, plus one per compareChannels entry)
  // instead of the per-signal-type breakdown above - colored by the
  // channel's own accent, not by signal name, since the point is
  // comparing channels' overall activity, not one channel's signal mix.
  // A channel whose data hasn't finished loading yet (ChartScreen fetches
  // each added channel's signals async) renders as a flat/empty line
  // labeled "(loading…)" rather than being silently omitted, so adding a
  // channel doesn't look like nothing happened while its fetch is in flight.
  const seriesByChannel = useMemo(() => {
    if (!comparing) return null;
    const primary = makeSeries(
      primaryChannel?.title || 'This channel',
      primaryChannel?.accentHex || FALLBACK_SIGNAL_COLOR,
      baseEntries
    );
    const others = compareChannels!.map((cc, i) => {
      const name = cc.channel.title || cc.channel.nickname;
      return makeSeries(cc.loading ? `${name} (loading…)` : name, cc.channel.accentHex || FALLBACK_SIGNAL_COLOR, compareEntries[i]);
    });
    return [primary, ...others];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [comparing, primaryChannel, compareChannels, buckets, activeMetric, baseEntries, compareEntries]);

  // The "by signal" compare view - see CompareViewMode's own comment.
  // Pools every compared channel's chartable entries and breaks them down
  // by signal type, instead of by channel. A signal name colors the same
  // everywhere it appears via the same `colorFor` lookup the single-channel
  // breakdown uses - two channels sharing a signal name/template end up as
  // one same-colored combined line, which is the point of "mixing" them.
  const seriesBySignalAcrossChannels = useMemo(() => {
    if (!comparing) return null;
    const pooled = [...baseEntries, ...compareEntries.flat()];
    const names = Array.from(new Set(pooled.map((e) => e.type)));
    return names.map((name) => makeSeries(name, colorFor(name), pooled.filter((e) => e.type === name)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [comparing, baseEntries, compareEntries, buckets, activeMetric]);

  // The "signal-split" compare view - see CompareViewMode's own comment.
  // Same per-signal-type breakdown and coloring as the pooled view above,
  // but keeps each channel's own entries in their own series instead of
  // merging same-named ones together, so two channels that happen to share
  // a signal template/name (or just similarly-named signals) never collapse
  // into a single combined line in the legend or on the chart. The channel
  // title is appended to the name to disambiguate same-named series in the
  // legend - `makeSeries`'s `name` is the only identity legend/hover/pin
  // logic keys off of, so two series can't otherwise share a label.
  const seriesBySignalPerChannel = useMemo(() => {
    if (!comparing) return null;
    const pools = [
      { title: primaryChannel?.title || 'This channel', entries: baseEntries },
      ...compareChannels!.map((cc, i) => ({
        title: cc.loading ? `${cc.channel.title || cc.channel.nickname} (loading…)` : cc.channel.title || cc.channel.nickname,
        entries: compareEntries[i],
      })),
    ];
    return pools.flatMap(({ title, entries }) => {
      const names = Array.from(new Set(entries.map((e) => e.type)));
      return names.map((name) => makeSeries(`${name} — ${title}`, colorFor(name), entries.filter((e) => e.type === name)));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [comparing, primaryChannel, compareChannels, baseEntries, compareEntries, buckets, activeMetric]);

  // Comparing channels always wins over the single-channel per-signal-type
  // breakdown - see `comparing`'s own comment - and, while comparing,
  // `compareViewMode` picks which of the three compare-shaped series to use.
  const multiSeries = comparing
    ? compareViewMode === 'signal'
      ? seriesBySignalAcrossChannels
      : compareViewMode === 'signal-split'
        ? seriesBySignalPerChannel
        : seriesByChannel
    : showingAllSignals
      ? seriesBySignal
      : null;
  const showingMulti = !!multiSeries && multiSeries.length > 0;
  // What the chart actually draws: every series except the ones switched off
  // with the legend's eye. The legend itself keeps listing all of them (so a
  // hidden one can be shown again).
  const hidden = useMemo(() => new Set(hiddenSeries), [hiddenSeries]);
  const drawnSeries = useMemo(() => (multiSeries ? multiSeries.filter((s) => !hidden.has(s.name)) : null), [multiSeries, hidden]);
  const allHidden = showingMulti && drawnSeries!.length === 0;
  // The two legend actions. "Only" (focus) hides all the others and, pressed
  // again, shows every series. The eye toggles just THIS series on/off and
  // leaves the rest as they are - so after focusing one series, pressing the
  // eye on another ADDS it to what is already shown.
  const isSolo = (name: string) =>
    !!multiSeries && multiSeries.length > 1 && !hidden.has(name) && multiSeries.every((s) => s.name === name || hidden.has(s.name));
  const showOnly = (name: string) =>
    setHiddenSeries(isSolo(name) ? [] : (multiSeries ?? []).filter((s) => s.name !== name).map((s) => s.name).slice(0, HIDDEN_SERIES_CAP));
  const toggleVisible = (name: string) =>
    setHiddenSeries(hidden.has(name) ? hiddenSeries.filter((n) => n !== name) : [...hiddenSeries, name].slice(0, HIDDEN_SERIES_CAP));
  // Guards against a stale pin/hover surviving a filter change that dropped
  // that series (e.g. it was hovered, then unselected in the signal filter) or
  // hid it with the eye.
  const activeHighlight = useMemo(() => {
    const name = hoveredSeries ?? pinnedSeries;
    return name && drawnSeries?.some((s) => s.name === name) ? name : null;
  }, [hoveredSeries, pinnedSeries, drawnSeries]);

  // A pinned selection is a (series key, bucket index) pair - stale once
  // the bucket layout, chart shape or series set it pointed at changes
  // underneath it (new interval, filter, compare mode, ...), so it's
  // cleared rather than silently pointing at the wrong bar/dot.
  useEffect(() => {
    setSelection([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bucketsSignature, chartType, showingMulti, activeMetric]);

  // With compared channels in the mix, "no signal messages" must check
  // every channel, not just this one - otherwise adding channels to an
  // otherwise-empty primary channel would hide the whole chart instead
  // of showing what was actually added.
  const hasAnyData = baseEntries.length > 0 || compareEntries.some((list) => list.length > 0);

  // Stacking min/max/average/latest across series adds numbers that don't
  // add up, so a multi-series "bar" falls back to lines for those metrics.
  const stackBars = isAdditiveMetric(activeMetric);
  // Latest/Average/Min/Max readings of different signals are usually different
  // units (CPU % vs load), so each gets its own scale by default; counts and
  // sums are comparable volumes, so they share one. The user's choice wins.
  const effectiveScale: ChartScale = scaleChoice || (stackBars ? 'shared' : 'each');
  const scalable = showingMulti && (chartType === 'line' || (chartType === 'bar' && !stackBars));

  return (
    <div className="flex flex-col gap-4">
      {/* The entire "Filters & settings" panel - interval/span/chart type/
          compare view/signal+sender filters/scale/metric - hidden in
          `locked` mode (public view, Channel.publicChartFiltersAllowed
          off). Maximize and tap-to-select-a-point below are unaffected;
          neither is a setting. */}
      {!locked && (
      <div className="flex flex-col gap-4">
        <button
          type="button"
          aria-expanded={controlsOpen}
          aria-controls="chart-controls"
          onClick={() => setControlsOpen(!controlsOpen)}
          className="flex items-center gap-2 text-left rounded-xl border border-black/10 dark:border-white/10 px-3 py-2"
        >
          <ChevronDown size={16} className={`shrink-0 transition-transform ${controlsOpen ? '' : '-rotate-90'}`} aria-hidden />
          <span className="text-sm font-semibold">Filters &amp; settings</span>
          {!controlsOpen && (
            <span className="text-xs text-black/45 dark:text-white/45 truncate">
              {[
                INTERVAL_LABELS[interval],
                capitalize(chartType),
                canChartByValue ? METRIC_LABELS[activeMetric] : null,
                selectedSignalTypes.length ? `${selectedSignalTypes.length} signal${selectedSignalTypes.length === 1 ? '' : 's'}` : null,
                selectedSenders.length ? `${selectedSenders.length} user${selectedSenders.length === 1 ? '' : 's'}` : null,
              ]
                .filter(Boolean)
                .join(' · ')}
            </span>
          )}
        </button>
        {controlsOpen && (
          <div id="chart-controls" className="flex flex-col gap-4">
      <SegmentedControl
        options={CHART_INTERVALS.map((i) => ({ value: i, label: INTERVAL_LABELS[i] }))}
        value={interval}
        onChange={setInterval}
      />
      {interval === 'Minutes' && (
        <SegmentedControl
          options={MINUTES_SPANS.map((s) => ({ value: s.value, label: s.label }))}
          value={minutesSpan}
          onChange={setMinutesSpan}
        />
      )}
      <SegmentedControl
        options={CHART_VARIANTS.map((v) => ({ value: v, label: capitalize(v) }))}
        value={chartType}
        onChange={onChartTypeChange}
      />

      {comparing && (
        <label className="flex flex-col gap-1">
          <span className="text-[11px] font-medium text-black/50 dark:text-white/50">Compare view</span>
          <SegmentedControl
            options={[
              { value: 'channel' as CompareViewMode, label: 'By channel' },
              { value: 'signal' as CompareViewMode, label: 'Signal (combined)' },
              { value: 'signal-split' as CompareViewMode, label: 'Signal (split)' },
            ]}
            value={compareViewMode}
            onChange={setCompareViewMode}
          />
        </label>
      )}

      {!comparing && availableSignals.length > 0 && (
        <MultiSelect
          placeholder="All signals"
          options={availableSignals.map((s) => ({ value: s, label: s }))}
          values={selectedSignalTypes}
          onChange={setSelectedSignalTypes}
        />
      )}

      {!comparing && availableSenders.length > 1 && (
        <MultiSelect
          placeholder="All users"
          options={availableSenders.map((s) => ({ value: s, label: s }))}
          values={selectedSenders}
          onChange={setSelectedSenders}
        />
      )}

      {scalable && (
        <label className="flex flex-col gap-1">
          <span className="text-[11px] font-medium text-black/50 dark:text-white/50">Scale</span>
          <SegmentedControl
            options={[
              { value: 'each' as ChartScale, label: 'Each signal' },
              { value: 'shared' as ChartScale, label: 'Shared axis' },
            ]}
            value={effectiveScale}
            onChange={setScaleChoice}
          />
        </label>
      )}

      {canChartByValue && (
        <SegmentedControl
          options={CHART_METRICS.map((m) => ({ value: m, label: METRIC_LABELS[m] }))}
          value={activeMetric}
          onChange={setMetric}
        />
      )}

          </div>
        )}
      </div>
      )}

      {renderChartCard(180)}

      <FullscreenOverlay open={fullscreen} onClose={() => setFullscreen(false)} title={comparing ? 'Comparing channels' : 'Signal activity'}>
        <div className="max-w-5xl mx-auto w-full">{renderChartCard(420)}</div>
      </FullscreenOverlay>
    </div>
  );

  // The chart card body, parameterized by height so the same markup (and
  // the same `selected`/`hoveredSeries`/`pinnedSeries` state) renders both
  // inline and - bigger, for "full detalization" - inside the fullscreen
  // overlay above. No element here carries an id, so mounting it twice
  // (inline + overlay, while `fullscreen` is open) never collides.
  function renderChartCard(chartHeight: number) {
    // A bucket/series the user clicked - resolved into its label + number(s)
    // for the detail panel below. Cleared (see the effect above) whenever
    // the data it pointed at could have shifted underneath it.
    const summary = summarizeSelection(buckets, selection);
    const pinnedBuckets = summary.groups.length;
    const additive = isAdditiveMetric(activeMetric);
    const seriesTotal = (values: number[]) => summary.groups.reduce((sum, g) => sum + (Number.isFinite(values[g.index]) ? values[g.index] : 0), 0);

    return (
      <AppCard className="flex flex-col gap-4">
        <div className="flex items-center justify-between gap-2">
          <span className="font-semibold">{comparing ? 'Comparing channels' : 'Signal activity'}</span>
          <div className="flex items-center gap-3">
            <span className="text-xs text-black/40 dark:text-white/40">
              {rollupLoading || compareRollupLoading ? '' : `${formatMetric(total)} ${METRIC_TOTAL_LABEL[activeMetric]}`}
            </span>
            {!fullscreen && (
              <button
                type="button"
                onClick={() => setFullscreen(true)}
                aria-label="Open chart full screen"
                title="Full screen"
                className="shrink-0 p-1 rounded-md text-black/40 dark:text-white/40 hover:text-current hover:bg-black/5 dark:hover:bg-white/10"
              >
                <Maximize2 size={15} />
              </button>
            )}
          </div>
        </div>

        {rollupLoading || compareRollupLoading ? (
          // The server rollup for THIS window hasn't arrived yet. Plotting the
          // handful of loaded messages meanwhile draws a misleading chart that
          // then jumps, so wait for the real numbers.
          <span className="text-sm text-black/40 dark:text-white/40 py-8 text-center" role="status">
            Loading chart…
          </span>
        ) : !hasAnyData ? (
          <span className="text-sm text-black/40 dark:text-white/40 py-8 text-center">
            No signal messages yet - send one from a template to see it charted here.
          </span>
        ) : (
          <>
            {chartType === 'number' && <span className="text-5xl font-bold">{formatMetric(total)}</span>}
            {allHidden && chartType !== 'number' && (
              <span className="text-sm text-black/40 dark:text-white/40 py-8 text-center" role="status">
                Every series is hidden - use the icons below to show them again.
              </span>
            )}
            {chartType === 'circle' && !allHidden &&
              (showingMulti ? (
                <MultiCircularMetric series={drawnSeries!.map((s) => ({ name: s.name, color: s.color, value: s.total }))} highlightKey={activeHighlight} />
              ) : (
                <CircularMetric progress={signalShare} label={`${Math.round(signalShare * 100)}%`} color={colorFor(singleSelectedType!)} />
              ))}
            {chartType === 'line' && !allHidden &&
              (showingMulti ? (
                <MultiLineChart
                  series={drawnSeries!}
                  height={chartHeight}
                  scale={effectiveScale}
                  highlightKey={activeHighlight}
                  selected={selection}
                  onSelect={toggleSelected}
                />
              ) : (
                <LineChart
                  values={counts}
                  height={chartHeight}
                  color={colorFor(singleSelectedType!)}
                  selectedIndexes={selection.filter((x) => x.key === 'single').map((x) => x.index)}
                  onSelect={(index) => toggleSelected('single', index)}
                />
              ))}
            {chartType === 'bar' && !allHidden &&
              (showingMulti ? (
                stackBars ? (
                  <StackedBarChart
                    series={drawnSeries!}
                    height={chartHeight}
                    highlightKey={activeHighlight}
                    selected={selection}
                    onSelect={toggleSelected}
                  />
                ) : (
                  <GroupedBarChart
                    series={drawnSeries!}
                    height={chartHeight}
                    scale={effectiveScale}
                    highlightKey={activeHighlight}
                    selected={selection}
                    onSelect={toggleSelected}
                  />
                )
              ) : (
                <BarChart
                  values={counts}
                  height={chartHeight}
                  color={colorFor(singleSelectedType!)}
                  selectedIndexes={selection.filter((x) => x.key === 'single').map((x) => x.index)}
                  onSelect={(index) => toggleSelected('single', index)}
                />
              ))}
            {(chartType === 'line' || chartType === 'bar') && !allHidden && (
              <div className="flex text-[10px] text-black/40 dark:text-white/40 px-0.5">
                {buckets.map((b, i) => (
                  <span key={i} className="flex-1 text-center truncate">
                    {b.label}
                  </span>
                ))}
              </div>
            )}
            {scalable && effectiveScale === 'each' && (
              <span className="text-[11px] text-black/40 dark:text-white/40 text-center">
                Each signal is scaled to its own range - exact values are in the list below.
              </span>
            )}
            {pinnedBuckets > 0 && (chartType === 'line' || chartType === 'bar') && (
              <div className="rounded-xl border border-black/10 dark:border-white/10 p-3 flex flex-col gap-2" data-testid="selection-panel">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex flex-col">
                    <span className="text-sm font-semibold">
                      {selection.length === 1 ? 'Selected' : `${selection.length} selected`}
                    </span>
                    {summary.overall && (
                      <span className="text-xs text-black/55 dark:text-white/55">
                        {pinnedBuckets > 1 ? `From ${summary.overall.range}` : summary.groups[0].range}
                      </span>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => setSelection([])}
                    aria-label="Clear selection"
                    className="shrink-0 inline-flex items-center gap-1 text-xs text-black/45 dark:text-white/45 hover:text-current"
                  >
                    {selection.length > 1 && <span>Clear all</span>}
                    <X size={14} />
                  </button>
                </div>
                {summary.groups.map((g) => (
                  <div key={g.index} className="flex flex-col gap-1 border-t border-black/5 dark:border-white/10 pt-2 first:border-t-0 first:pt-0">
                    {pinnedBuckets > 1 && <span className="text-xs font-medium text-black/60 dark:text-white/60">{g.range}</span>}
                    {showingMulti ? (
                      drawnSeries!.map((s) => {
                        const pinned = g.keys.includes(s.name);
                        return (
                          <div key={s.name} className="flex items-center gap-2 text-sm">
                            <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: s.color }} />
                            <MarqueeText
                              text={s.name}
                              active={pinned}
                              className={pinned ? 'font-semibold' : 'text-black/70 dark:text-white/70'}
                            />
                            <span className="flex-1" />
                            <span className={`tabular-nums ${pinned ? 'font-semibold' : 'text-black/45 dark:text-white/45'}`}>
                              {formatMetric(s.values[g.index] ?? 0)}
                            </span>
                          </div>
                        );
                      })
                    ) : (
                      <span className={`${pinnedBuckets > 1 ? 'text-lg' : 'text-2xl'} font-bold tabular-nums`}>
                        {formatMetric(counts[g.index] ?? 0)}
                      </span>
                    )}
                  </div>
                ))}
                {pinnedBuckets > 1 && additive && (
                  <div className="flex flex-col gap-1 border-t border-black/10 dark:border-white/10 pt-2" data-testid="selection-total">
                    <span className="text-xs font-medium text-black/60 dark:text-white/60">Total across {pinnedBuckets} periods</span>
                    {showingMulti ? (
                      drawnSeries!.map((s) => (
                        <div key={s.name} className="flex items-center gap-2 text-sm">
                          <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: s.color }} />
                          <span className="truncate">{s.name}</span>
                          <span className="flex-1" />
                          <span className="tabular-nums font-semibold">{formatMetric(seriesTotal(s.values))}</span>
                        </div>
                      ))
                    ) : (
                      <span className="text-2xl font-bold tabular-nums">{formatMetric(seriesTotal(counts))}</span>
                    )}
                  </div>
                )}
              </div>
            )}
            {showingMulti && (
              <div className="flex flex-col gap-1.5" onMouseLeave={() => setHoveredSeries(null)}>
                {multiSeries!.map((s) => {
                  const isHidden = hidden.has(s.name);
                  const active = !isHidden && activeHighlight === s.name;
                  return (
                    <div key={s.name} role="group" aria-label={s.name} className="flex items-center gap-1" onMouseLeave={() => setHoveredSeries(null)}>
                      {/* Two quick filters: show only this series, or toggle just
                          this one on/off (adds it back after a focus). Kept
                          apart from the row button, which still highlights. */}
                      <FeatureGate feature="charts.legend_controls" fallback={null}>
                      <button
                        type="button"
                        onClick={() => showOnly(s.name)}
                        aria-pressed={isSolo(s.name)}
                        aria-label={isSolo(s.name) ? 'Show all' : 'Show only this'}
                        title={isSolo(s.name) ? 'Show all' : 'Hide all others, show only this'}
                        className={`shrink-0 p-1 rounded-md hover:text-current hover:bg-black/5 dark:hover:bg-white/10 ${
                          isSolo(s.name) ? 'text-current' : 'text-black/45 dark:text-white/45'
                        }`}
                      >
                        <Focus size={15} />
                      </button>
                      <button
                        type="button"
                        onClick={() => toggleVisible(s.name)}
                        aria-pressed={isHidden}
                        aria-label={isHidden ? 'Show this' : 'Hide this'}
                        title={isHidden ? 'Add this back to the chart' : 'Hide this, keep the others as they are'}
                        className={`shrink-0 p-1 rounded-md hover:text-current hover:bg-black/5 dark:hover:bg-white/10 ${
                          isHidden ? 'text-current' : 'text-black/45 dark:text-white/45'
                        }`}
                      >
                        <EyeOff size={15} />
                      </button>
                      </FeatureGate>
                      <button
                        type="button"
                        onMouseEnter={() => setHoveredSeries(s.name)}
                        onFocus={() => setHoveredSeries(s.name)}
                        onBlur={() => setHoveredSeries(null)}
                        onClick={() => setPinnedSeries((p) => (p === s.name ? null : s.name))}
                        aria-pressed={pinnedSeries === s.name}
                        className={`flex items-center gap-2 flex-1 min-w-0 text-sm text-left rounded-lg px-1 py-0.5 transition-colors ${
                          active ? 'bg-black/[0.04] dark:bg-white/[0.08]' : ''
                        } ${isHidden ? 'opacity-40' : activeHighlight != null && !active ? 'opacity-50' : ''}`}
                      >
                        <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: s.color }} />
                        <MarqueeText text={s.name} active={active} className={`${active ? 'font-semibold' : ''} ${isHidden ? 'line-through' : ''} text-black/70 dark:text-white/70`} />
                        <span className="flex-1" />
                        <span className="tabular-nums text-black/45 dark:text-white/45">{formatMetric(s.total)}</span>
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
            {chartType === 'circle' && !showingMulti && (
              <span className="text-xs text-black/40 dark:text-white/40 text-center">
                share of all messages that are signals
              </span>
            )}
          </>
        )}
      </AppCard>
    );
  }
}
